import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Card,
    CardContent,
    Avatar,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import SparaLoader from 'components/SparaLoader/SparaLoader';
import useSWR from 'swr';
import { fetcher, offerApi } from '../../../lib/api';
import formatWithThousandSeparator from '../../../utils/thousand_separator';
import styles from './NotificationModal.module.scss';

const NotificationModal = ({
    open,
    onClose,
    onSend,
    offer,
    t,
    onTotalCountChange,
    isSuperAdmin = false,
}) => {
    const [includeUnregistered, setIncludeUnregistered] = useState(false);
    const [selectedGroupIds, setSelectedGroupIds] = useState([]);
    const [costConfirmed, setCostConfirmed] = useState(false);
    const [groupsInitialized, setGroupsInitialized] = useState(false);

    const isOpenOffer = Boolean(offer && !offer.group_ids);
    const isRetry = Boolean(offer?.notified);

    const groupQuery = useMemo(() => {
        if (!selectedGroupIds.length) return '';
        return selectedGroupIds.join(',');
    }, [selectedGroupIds]);

    const shouldFetch = Boolean(open && offer);
    const { data, error, isLoading } = useSWR(
        shouldFetch
            ? offerApi.notificationCount(offer.id, {
                  includeUnregistered: includeUnregistered && isOpenOffer && isSuperAdmin,
                  groupIds: isOpenOffer ? null : groupQuery || null,
              })
            : null,
        fetcher
    );

    const results = data?.results;
    const groups = results?.groups || [];

    useEffect(() => {
        if (!open) {
            setIncludeUnregistered(false);
            setSelectedGroupIds([]);
            setCostConfirmed(false);
            setGroupsInitialized(false);
            return;
        }
        setCostConfirmed(false);
    }, [open, offer?.id]);

    useEffect(() => {
        if (!open || isOpenOffer || groupsInitialized) return;
        if (!results?.groups) return;
        const selectable = (results.groups || [])
            .filter((g) => g.selectable)
            .map((g) => Number(g.id));
        setSelectedGroupIds(selectable);
        setGroupsInitialized(true);
    }, [open, isOpenOffer, groupsInitialized, results?.groups]);

    const count = results?.count ?? null;
    const registeredCount = results?.registered_count ?? null;
    const unregisteredCount = results?.unregistered_count ?? null;
    const unitPrice = results?.unit_price_isk ?? 8;
    const estimatedAmount = results?.estimated_amount_isk ?? 0;
    const isBillable = results?.is_billable ?? false;
    const billable = results?.billable;
    const notificationPreview = results
        ? {
              title: results.notification_title,
              description: results.notification_description,
              image: results.notification_image,
          }
        : null;

    useEffect(() => {
        if (count !== null && onTotalCountChange) {
            onTotalCountChange(count);
        }
    }, [count, onTotalCountChange]);

    const formattedCount = count !== null ? formatWithThousandSeparator(count) : '';

    const handleClose = () => {
        setIncludeUnregistered(false);
        setSelectedGroupIds([]);
        setCostConfirmed(false);
        setGroupsInitialized(false);
        onClose();
    };

    const toggleGroup = (id) => {
        const gid = Number(id);
        setSelectedGroupIds((prev) =>
            prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid]
        );
        setCostConfirmed(false);
    };

    const canSend =
        !isLoading &&
        count > 0 &&
        (isOpenOffer || selectedGroupIds.length > 0) &&
        (!isBillable || costConfirmed);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            data-testid="notification-modal"
            PaperProps={{
                sx: {
                    width: { xs: '95vw', sm: 500 },
                    minWidth: { xs: '95vw', sm: 500 },
                    maxWidth: { xs: '95vw', sm: 500 },
                    height: { xs: 'auto', sm: 'auto' },
                    maxHeight: { xs: '90vh', sm: '80vh' },
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle
                data-testid="notification-modal-title"
                sx={{
                    textAlign: 'left',
                    fontWeight: 600,
                    fontStyle: 'italic',
                    color: '#DE2C48',
                }}
            >
                {isRetry
                    ? t
                        ? t('notification.retry_title')
                        : 'Senda á þá sem eftir eru'
                    : t
                      ? t('send_notification')
                      : 'Senda tilkynningu'}
            </DialogTitle>
            <DialogContent
                sx={{
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    p: 3,
                }}
            >
                {isLoading && !results ? (
                    <SparaLoader text={t ? t('common.loading') : 'Sæki fjölda...'} />
                ) : error ? (
                    <Typography color="error" align="center">
                        {t ? t('error_fetching_count') : 'Villa við að sækja fjölda'}
                    </Typography>
                ) : (
                    <>
                        {notificationPreview && (
                            <Box data-testid="notification-preview">
                                <Typography sx={{ mb: 2, fontWeight: 600, fontStyle: 'italic' }}>
                                    {t ? t('notification_preview') : 'Forskoðun tilkynningar'}
                                </Typography>
                                <Card
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 4,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        backgroundColor: '#f8f9fa',
                                    }}
                                >
                                    <CardContent sx={{ p: 2 }}>
                                        <Box display="flex" alignItems="flex-start" gap={2}>
                                            <Avatar
                                                src="/spara-app-icon.png"
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: 3.5,
                                                    backgroundColor: '#fff',
                                                }}
                                            />
                                            <Box flex={1}>
                                                <Typography
                                                    data-testid="notification-preview-title"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#333',
                                                        mb: 1,
                                                    }}
                                                >
                                                    {notificationPreview.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    data-testid="notification-preview-description"
                                                    sx={{
                                                        color: '#666',
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {notificationPreview.description}
                                                </Typography>
                                            </Box>
                                            {notificationPreview.image && (
                                                <Avatar
                                                    src={notificationPreview.image}
                                                    data-testid="notification-preview-image"
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        borderRadius: 2,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}

                        {!isOpenOffer && groups.length > 0 && (
                            <Box data-testid="notification-groups">
                                <Typography
                                    sx={{ mb: 1.5, fontWeight: 600 }}
                                    data-testid="notification-select-groups-label"
                                >
                                    {t ? t('notification.select_groups') : 'Veldu hópa'}
                                </Typography>
                                <div className={styles.groupList}>
                                    {groups.map((group) => {
                                        const gid = Number(group.id);
                                        const selected = selectedGroupIds.includes(gid);
                                        return (
                                            <button
                                                key={gid}
                                                type="button"
                                                className={`${styles.groupPill} ${
                                                    selected ? styles.groupPillSelected : ''
                                                }`}
                                                onClick={() => toggleGroup(gid)}
                                                aria-pressed={selected}
                                                data-testid={`notification-group-pill-${gid}`}
                                            >
                                                {selected && (
                                                    <span
                                                        className={styles.groupPillCheck}
                                                        data-testid={`notification-group-check-${gid}`}
                                                    >
                                                        <Check />
                                                    </span>
                                                )}
                                                <span>{group.name}</span>
                                                <span className={styles.groupPillCount}>
                                                    (
                                                    {formatWithThousandSeparator(
                                                        group.member_count || 0
                                                    )}
                                                    )
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Box>
                        )}

                        {isOpenOffer && isSuperAdmin && (
                            <FormControlLabel
                                data-testid="notification-include-unregistered"
                                control={
                                    <Checkbox
                                        checked={includeUnregistered}
                                        onChange={(e) => setIncludeUnregistered(e.target.checked)}
                                        color="primary"
                                        inputProps={{
                                            'data-testid': 'notification-include-unregistered-input',
                                        }}
                                    />
                                }
                                label={
                                    t
                                        ? t('include_unregistered_users')
                                        : 'Senda á óskráða notendur'
                                }
                            />
                        )}

                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            data-testid="notification-count-section"
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                pt: 2,
                            }}
                        >
                            <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                                {t
                                    ? t('notification_count_label')
                                    : 'Fjöldi notenda sem fá tilkynningu:'}
                            </Typography>
                            <Typography
                                variant="h2"
                                align="center"
                                data-testid="notification-count-value"
                                sx={{
                                    color: '#DE2C48',
                                    fontWeight: 600,
                                    fontSize: { xs: '2.5rem', sm: '2.5rem' },
                                }}
                            >
                                {formattedCount}
                            </Typography>
                            {includeUnregistered &&
                                isOpenOffer &&
                                registeredCount != null &&
                                unregisteredCount != null && (
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{ mt: 1, color: '#666' }}
                                    >
                                        {t
                                            ? t('notification_count_breakdown', {
                                                  registered:
                                                      formatWithThousandSeparator(registeredCount),
                                                  unregistered:
                                                      formatWithThousandSeparator(
                                                          unregisteredCount
                                                      ),
                                              })
                                            : `${formatWithThousandSeparator(registeredCount)} skráðir, ${formatWithThousandSeparator(unregisteredCount)} óskráðir`}
                                    </Typography>
                                )}

                            {isBillable && (
                                <Box
                                    sx={{ mt: 2, width: '100%', textAlign: 'center' }}
                                    data-testid="notification-billing"
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600 }}
                                        data-testid="notification-estimated-amount"
                                    >
                                        {formatWithThousandSeparator(estimatedAmount)} kr.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        data-testid="notification-unit-price"
                                    >
                                        {formatWithThousandSeparator(unitPrice)} kr. án VSK ×{' '}
                                        {formattedCount}
                                    </Typography>
                                    {billable?.name && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 1 }}
                                            data-testid="notification-billable-to"
                                        >
                                            {t
                                                ? t('notification.billable_to', {
                                                      name: billable.name,
                                                  })
                                                : `Reikningur: ${billable.name}`}
                                            {billable.kennitala ? ` (${billable.kennitala})` : ''}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mt: 1 }}
                                        data-testid="notification-billing-note"
                                    >
                                        {t
                                            ? t('notification.billing_note')
                                            : 'Endanleg upphæð miðast við skilaboð sem Firebase tekur við. Reikningur er sendur í lok mánaðar.'}
                                    </Typography>
                                    <FormControlLabel
                                        sx={{ mt: 1 }}
                                        data-testid="notification-confirm-cost"
                                        control={
                                            <Checkbox
                                                checked={costConfirmed}
                                                onChange={(e) => setCostConfirmed(e.target.checked)}
                                                color="primary"
                                                inputProps={{
                                                    'data-testid':
                                                        'notification-confirm-cost-input',
                                                }}
                                            />
                                        }
                                        label={
                                            t
                                                ? t('notification.confirm_cost')
                                                : 'Ég staðfesti kostnaðinn'
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                    onClick={handleClose}
                    color="secondary"
                    variant="outlined"
                    data-testid="notification-cancel"
                >
                    {t ? t('cancel') : 'Hætta við'}
                </Button>
                <Button
                    onClick={() =>
                        onSend({
                            includeUnregistered: includeUnregistered && isOpenOffer && isSuperAdmin,
                            groupIds: isOpenOffer ? [] : selectedGroupIds,
                            isRetry,
                        })
                    }
                    color="primary"
                    variant="contained"
                    disabled={!canSend}
                    data-testid="notification-send"
                >
                    {isRetry
                        ? t
                            ? t('notification.retry_send')
                            : 'Senda á þá sem eftir eru'
                        : t
                          ? t('send')
                          : 'Senda'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationModal;
